import type { ConditionOperatorType } from '@pushly/pquery'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'
import {
    readsListAsTermSet,
    SCALAR_WRAPPING_SET_OPERATORS,
    valueShapeFor,
} from '@pushly/aqe/lib/components/pquery-builder'

/**
 * Whether a condition's value is entered as a set of values or as one.
 *
 * The builder answers that from the shape the engine reads the value as, and this asks the
 * builder's own table rather than restating an operator list beside it: a segmentation-local
 * renderer replaces the control the builder would have shown, so the two have to agree about
 * arity or the same operator collects a set in one place and a single term in the other — and
 * the value a reader authors is then one the validator rejects, or one the engine reads as a
 * different audience.
 *
 * Both set-valued shapes are included, for the same reason the builder's own control mapping
 * includes both: a set of document identifiers is entered exactly the way a set of terms is.
 * They stay separate shapes so a value cannot be carried between them, which is a different
 * question from which control enters one.
 *
 * The value is consulted only for the case the engine forces: it reads a list under `eq`/`neq`
 * on a scalar-valued field as the same term set `eq_any` builds, so a stored one is a correct
 * document and must be editable as the set it is instead of collapsing into one joined string
 * the reader cannot take apart. That is a value the shape's own rule accepts, not a second
 * answer to the arity question.
 */
export function rendersTermSet(
    operator: ConditionOperatorType,
    field: PQueryBuilderField | undefined,
    value: unknown,
): boolean {
    const shape = valueShapeFor(operator, field)

    if (shape === 'list' || shape === 'document-ids') return true

    return Array.isArray(value) && readsListAsTermSet(operator, field)
}

/**
 * The stored value as the entries a multi-entry control shows.
 *
 * A set-valued condition may be stored as a bare scalar — the engine wraps a lone value into a
 * one-entry set — so a scalar is one entry rather than a value this control cannot read. Showing
 * it is what keeps the id or term a segment is already built on in front of the reader instead of
 * opening the row on an empty box that drops it on the next edit.
 *
 * An absent or empty scalar is no entry at all: the shape's own rule reports the gap, where a
 * blank entry would read as a value someone chose.
 */
export function readTermSet<T extends string | number>(value: T | T[] | null | undefined): T[] {
    if (value === null || value === undefined) return []
    if (Array.isArray(value)) return value
    if (typeof value === 'string' && value === '') return []
    return [value]
}

/**
 * The entries a set-valued condition holds, read from a value whose shape is not known ahead of
 * time and stated as the strings they are displayed as.
 *
 * A row summary is handed the stored value untyped, so the narrowing belongs here beside
 * {@link readTermSet} rather than at the caller. Both shapes a stored condition can hold are read:
 * the bare scalar the engine wraps into a one-entry set, and the list.
 *
 * Anything that is not a term is dropped rather than stringified. `undefined` and `null` have no
 * spelling a reader would recognise, and an empty string is no entry at all — showing either would
 * put a value on the row that nobody chose.
 */
export function readTermSetAsStrings(value: unknown): string[] {
    const entries: unknown[] = Array.isArray(value) ? value : [value]

    return entries
        .filter((entry): entry is string | number => typeof entry === 'number' || typeof entry === 'string')
        .map(String)
        .filter((entry) => entry !== '')
}

/**
 * The value a multi-entry control emits for the entries it holds.
 *
 * A lone entry is emitted as the bare scalar only under the operators whose clause wraps a scalar
 * into a one-entry set on every backend a segment document reaches — the builder's own
 * `SCALAR_WRAPPING_SET_OPERATORS`, asked here rather than restated, so the two cannot disagree
 * about which operators that is. Where it holds, the scalar is the better document: nothing
 * downstream unwraps a one-entry list, so emitting one would rewrite every stored scalar the
 * account already holds and make merely opening a row report unsaved changes on a segment nobody
 * edited. The engine reads the two identically, so the choice costs no audience.
 *
 * Every other set-valued operator emits the one-entry list, because the wrap is not universal:
 * `eq_any`/`neq_any` are wrapped by OpenSearch, but Snowflake builds `field IN (...)` from a list
 * comprehension over the value and raises on a scalar — so a row collapsed to a scalar there
 * authors a query that cannot run at all. The collapse must never be widened past the operators
 * whose wrap has been confirmed in every factory the value can reach.
 */
export function emitTermSet<T>(operator: ConditionOperatorType, entries: T[]): T | T[] {
    if (entries.length === 1 && SCALAR_WRAPPING_SET_OPERATORS.has(operator)) return entries[0]
    return entries
}
